import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from '@material-ui/core';

import downIcon from '../../assets/images/down-chevron.svg';
import upIcon from '../../assets/images/up-chevron.svg';
import favoritewhite from '../../assets/images/favoritewhite.svg';
import favoriteyellow from '../../assets/images/favoriteyellow.svg';
import { getIndividualAvatar, getTeamAvatar } from '../../utils/avatarImages';

import config from '../../config';
import { handleResponse } from '../../utils/handleResponse';
import RankingParticipantLabel from '../../components/RankingParticipantLabel';
import {
  LAP_DISTANCE_KM,
  formatDistanceKm,
  snap12hTotalKm,
} from '../../utils/utils';
import { Row, Col } from 'react-flexbox-grid';

const formatTimeHHMMSS = (totalSeconds) => {
  if (!totalSeconds) return '00:00:00';
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = Math.floor(totalSeconds % 60);
  const hh = h < 10 ? `0${h}` : h;
  const mm = m < 10 ? `0${m}` : m;
  const ss = s < 10 ? `0${s}` : s;
  return `${hh}:${mm}:${ss}`;
};

const formatPaceMMSS = (paceSeconds) => {
  if (!paceSeconds || paceSeconds <= 0) return '--:--';
  const min = Math.floor(paceSeconds / 60);
  const sec = Math.floor(paceSeconds % 60);
  const mm = min < 10 ? `0${min}` : min;
  const ss = sec < 10 ? `0${sec}` : sec;
  return `${mm}:${ss}`;
};

const TableRankingTeamItem = ({
  userInfo,
  firstRaking,
  darkMode,
  teamFavorites,
  saveTeamFavorite,
  deleteTeamFavorite,
  setShowLoading,
  favoritesEnabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [laps, setLaps] = useState([]);
  const [fastestPace, setFastestPace] = useState(null);
  const [slowestPace, setSlowestPace] = useState(null);

  const raceStatus = userInfo.race_status || userInfo.raceStatus;
  const participants = userInfo.participants || [];

  useEffect(() => {
    if (laps.length > 0) {
      const paces = laps.map((lap) => lap.elapsedTime / LAP_DISTANCE_KM);
      setFastestPace(Math.min(...paces));
      setSlowestPace(Math.max(...paces));
    } else {
      setFastestPace(null);
      setSlowestPace(null);
    }
  }, [laps]);

  const fetchTeamLaps = async (teamId) => {
    try {
      if (setShowLoading) setShowLoading(true);
      const response = await fetch(
        `${config.tncApiUrl}/team/${teamId}/laps`,
        { method: 'GET' }
      );
      if (setShowLoading) setShowLoading(false);
      const data = await handleResponse(response);
      return data?.data?.laps || [];
    } catch (error) {
      console.error('Error fetching team laps:', error);
      if (setShowLoading) setShowLoading(false);
      return [];
    }
  };

  const toggleAccordion = async () => {
    if (!isOpen) {
      const loadedLaps = await fetchTeamLaps(userInfo.id);
      setLaps(loadedLaps);
      setIsOpen(true);
    } else {
      setIsOpen(false);
      setLaps([]);
    }
  };

  // Total del equipo viene del ranking (API); no recalcular con vueltas del acordeón.
  const displayDistanceKm = snap12hTotalKm(userInfo.total_distance);

  const addButtonText = () => {
    if (!favoritesEnabled) return null;
    let favoriteIcon = favoritewhite;
    let saveFunction = saveTeamFavorite;
    let favoriteId = userInfo.id;

    if (teamFavorites) {
      teamFavorites.forEach((teamFavorite) => {
        if (teamFavorite.teamId === userInfo.id) {
          favoriteIcon = favoriteyellow;
          favoriteId = teamFavorite.teamId;
          saveFunction = deleteTeamFavorite;
        }
      });
    }
    return (
      <div
        onClick={(event) => {
          event.stopPropagation();
          saveFunction(favoriteId);
        }}
        className='add-button'
        role='presentation'
      >
        <img src={favoriteIcon} className='favorite-icon' alt='favoriteIcon' />
      </div>
    );
  };

  const calculateAvgPace = (elapsed_time, distanceKm) => {
    if (!elapsed_time || distanceKm <= 0) {
      return (
        <Tooltip
          enterTouchDelay={100}
          title='No hay datos disponibles'
          placement='top'
        >
          <span>⚠️</span>
        </Tooltip>
      );
    }
    return formatPaceMMSS(elapsed_time / distanceKm);
  };

  const showTime = (elapsed_time) => {
    if (!elapsed_time) {
      return (
        <Tooltip
          enterTouchDelay={100}
          title='Esperando tiempo o sin datos'
          placement='top'
        >
          <span>⚠️</span>
        </Tooltip>
      );
    }

    const mainTime = formatTimeHHMMSS(elapsed_time);
    let diffTime = null;

    if (
      firstRaking &&
      firstRaking.id !== userInfo.id &&
      firstRaking.elapsed_time
    ) {
      const diff = Math.abs(elapsed_time - firstRaking.elapsed_time);
      diffTime = formatTimeHHMMSS(diff);
    }

    return (
      <div className='ranking-time-values'>
        <span className='ranking-time-values__main'>{mainTime}</span>
        {diffTime && (
          <div className='table-ranking-item_user-info-team'>
            <span>+ {diffTime}</span>
          </div>
        )}
      </div>
    );
  };

  const handleRowKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleAccordion();
    }
  };

  const renderTeamLaps = () => {
    if (laps.length === 0) {
      return (
        <Row className='table-ranking-item_column-container_secundary individual team-accordion-empty'>
          <Col xs={12} className='table-ranking-item_column ranking-lap-name'>
            <div className='table-ranking-item_user-info-name'>
              No hay vueltas registradas
            </div>
          </Col>
        </Row>
      );
    }

    return laps
      .slice()
      .sort((a, b) => a.lapNumber - b.lapNumber)
      .map((lap, index) => {
        const paceSec = lap.elapsedTime / LAP_DISTANCE_KM;
        const isFastest = paceSec === fastestPace;
        const isSlowest = paceSec === slowestPace;
        const paceClass =
          isFastest && isSlowest
            ? ''
            : isFastest
            ? ' ranking-lap-value--fast'
            : isSlowest
            ? ' ranking-lap-value--slow'
            : '';

        return (
          <Row
            key={lap.id || index}
            className='table-ranking-item_column-container_secundary individual'
          >
            <Col xs={5} className='table-ranking-item_column ranking-lap-name'>
              <div className='table-ranking-item_user-info-name ranking-lap-prefix'>
                LAP/
              </div>
              <div className='position-text ranking-lap-index'>
                {lap.lapNumber}
              </div>
            </Col>
            <Col xs={2} md={2} className='table-ranking-item_column stat-column'>
              <div
                className={`table-ranking-item_user-info-name ranking-lap-value${paceClass}`}
              >
                {formatDistanceKm(LAP_DISTANCE_KM)}
              </div>
            </Col>
            <Col xs={2} className='table-ranking-item_column stat-column'>
              <div
                className={`table-ranking-item_user-info-name ranking-lap-value${paceClass}`}
              >
                {formatPaceMMSS(paceSec)}
              </div>
            </Col>
            <Col xs={3} md={2} className='table-ranking-item_column stat-column'>
              <div
                className={`table-ranking-item_user-info-name ranking-lap-value${paceClass}`}
              >
                {formatTimeHHMMSS(lap.elapsedTime)}
              </div>
            </Col>
          </Row>
        );
      });
  };

  return (
    <Col
      xs={12}
      className={`table-ranking-item${darkMode ? ' darkMode' : ''}${
        isOpen ? ' table-ranking-item--expanded' : ''
      }`}
    >
      <Row className='table-ranking-item_column-container'>
        <Col xs={12}>
          <Row
            className={`table-ranking-item_column-container_main is-clickable${
              favoritesEnabled ? '' : ' no-favorites'
            }${isOpen ? ' is-open' : ''}`}
            onClick={toggleAccordion}
            onKeyDown={handleRowKeyDown}
            role='button'
            tabIndex={0}
            aria-expanded={isOpen}
          >
            <Col xs={5} md={6} className='table-ranking-item_column name-column'>
              <div className='table-ranking-item_leading'>
                {addButtonText()}
                <div className='position-text'>{userInfo.ranking}</div>
                <div className='table-ranking-item_user-img-container avatar-12h-container'>
                  <img
                    src={getTeamAvatar(userInfo.gender)}
                    alt=''
                    className='table-ranking-item_user-img avatar-12h'
                  />
                </div>
              </div>
              <div className='table-ranking-item_user-info-container'>
                <div className='table-ranking-item_user-info-name'>
                  {userInfo.name}
                </div>
              </div>
            </Col>

            <Col xs={2} md={2} className='table-ranking-item_column stat-column'>
              <div className='table-ranking-item_user-info-name'>
                {raceStatus === 3 || raceStatus === 4
                  ? '--'
                  : formatDistanceKm(displayDistanceKm)}
              </div>
            </Col>

            <Col xs={2} className='table-ranking-item_column stat-column'>
              <div className='table-ranking-item_user-info-name'>
                {raceStatus === 3 || raceStatus === 4
                  ? '--'
                  : calculateAvgPace(userInfo.elapsed_time, displayDistanceKm)}
              </div>
            </Col>

            <Col
              xs={3}
              md={2}
              className='table-ranking-item_column stat-column table-ranking-item_column--time'
            >
              <div className='table-ranking-item_time-cell'>
                <div className='ranking-time-stack'>
                  <div className='ranking-time-stack__values'>
                    {raceStatus === 3
                      ? 'DNF'
                      : raceStatus === 4
                      ? 'DQ'
                      : showTime(userInfo.elapsed_time)}
                  </div>
                  <img
                    src={isOpen ? upIcon : downIcon}
                    alt=''
                    className='down-icon ranking-time-chevron hide-mobile'
                    aria-hidden
                  />
                  <img
                    src={isOpen ? upIcon : downIcon}
                    alt=''
                    className='down-icon ranking-time-chevron hide-desktop'
                    aria-hidden
                  />
                </div>
              </div>
            </Col>
          </Row>
        </Col>

        {isOpen && (
          <Col xs={12} className='team-accordion'>
            {participants.length > 0 && (
              <section
                className='team-roster team-roster--grid'
                aria-label='Integrantes del equipo'
              >
                <p className='team-roster__label'>
                  <span className='team-roster__label-title'>Integrantes</span>
                  <span className='team-roster__entity-name'>{userInfo.name}</span>
                </p>
                <ul className='team-roster__grid'>
                  {participants.map((participant) => (
                    <li key={participant.id} className='team-roster__chip'>
                      <div className='team-roster__avatar'>
                        <img
                          src={getIndividualAvatar(participant.gender)}
                          alt=''
                        />
                      </div>
                      <RankingParticipantLabel
                        person={participant}
                        variant='team'
                        className='team-roster__fullname'
                        nameClassName='team-roster__name'
                        bibClassName='team-roster__bib'
                      />
                    </li>
                  ))}
                </ul>
              </section>
            )}
            <div className='team-accordion-laps'>{renderTeamLaps()}</div>
          </Col>
        )}
      </Row>
    </Col>
  );
};

TableRankingTeamItem.propTypes = {
  userInfo: PropTypes.object.isRequired,
  firstRaking: PropTypes.object,
  darkMode: PropTypes.bool,
  teamFavorites: PropTypes.array,
  saveTeamFavorite: PropTypes.func,
  deleteTeamFavorite: PropTypes.func,
  setShowLoading: PropTypes.func,
  favoritesEnabled: PropTypes.bool,
};

TableRankingTeamItem.defaultProps = {
  darkMode: false,
  teamFavorites: [],
  favoritesEnabled: false,
  setShowLoading: null,
};

export default TableRankingTeamItem;
